import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Drawer, Icon } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Heading, Regular } from "capitalrx-components-2.0/lib/typography";
import styled, { createGlobalStyle } from "styled-components";

import { ClientAwareLink } from "@/App/components";
import { MobileMainBar } from "@/App/components/MenuBar/MobileMenuBar/MobileMainBar";
import { MobileTopBar } from "@/App/components/MenuBar/MobileMenuBar/MobileTopBar";
import { MemberEligibilityContext } from "@/App/context";

import { ThemedLink } from "../../themed";
import { MenuItem } from "../MenuBar";

const NestedMenuHeadingButton = styled(Button)`
  &.tertiary {
    padding: 0;
    path {
      fill: #0071b3 !important;
    }
  }

  .heading {
    color: #0071b3;
    text-decoration: underline;
    text-decoration-thickness: from-font;
  }

  .shelf {
    justify-content: left;
    gap: 1em;
    margin-left: -6px;
  }
`;

const StyledMenuButton = styled(Button)`
  &.tertiary {
    padding: 0;
    color: #313e48;
    path {
      fill: #313e48 !important;
    }
  }

  .shelf {
    justify-content: space-between;
  }
`;

const ClientAwareMenuLink = styled(ClientAwareLink)`
  color: #313e48 !important;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: #0071b3 !important;
  }
`;

const NestedClientAwareMenuLink = styled(ClientAwareLink)`
  color: #313e48 !important;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const MenuLink = styled(ThemedLink)`
  color: #313e48 !important;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }

  &:hover {
    color: #0071b3 !important;
  }

  path {
    fill: #0071b3 !important;
  }
`;

const NestedMenuLink = styled(ThemedLink)`
  color: #313e48 !important;
  text-decoration: none;

  path {
    fill: #0071b3 !important;
  }

  &:hover {
    text-decoration: underline;
  }
`;

interface MobileMenuDrawerProps {
  isVisible: boolean;
  setVisible: (visible: boolean) => void;
  homeItems: MenuItem[];
  dynamicLinkItems: MenuItem[];
  externalConnectionItems: MenuItem[];
  claimsAndCoverageMenuItems: MenuItem[];
  careSearchMenuItems: MenuItem[];
  onCommonMenuItemClick: (menuItem: MenuItem) => void;
}

const DrawerOffsetStyle = createGlobalStyle<{ spaceForViewer: boolean }>`
  dialog.drawer.position-right {
    top: ${(props) => (props.spaceForViewer ? "152px" : "114px")} !important; /* or whatever offset you need */
    box-shadow: none;
  }

  /* Remove the native dialog backdrop */
  dialog.drawer::backdrop {
      background: none !important;
  }

  /* Optional: remove any custom overlay container too */
  .overlay-container,
  .overlay-container-content {
      background: none !important;
      pointer-events: none !important;
  }
`;

export const MobileMenuDrawer = ({
  isVisible,
  setVisible,
  homeItems,
  dynamicLinkItems,
  externalConnectionItems,
  claimsAndCoverageMenuItems,
  careSearchMenuItems,
  onCommonMenuItemClick,
}: MobileMenuDrawerProps) => {
  const [claimsAndCoverageMenuVisible, setClaimsAndCoverageMenuVisible] = useState(false);
  const [careSearchMenuVisible, setCareSearchMenuVisible] = useState(false);
  const { t: translate } = useTranslation();
  const { availableEligibility } = useContext(MemberEligibilityContext);
  const [showTopMenus, setShowTopMenus] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const timeout = setTimeout(() => {
        setShowTopMenus(true);
      }, 300); // match the drawer's transition duration
      return () => clearTimeout(timeout);
    } else {
      setShowTopMenus(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (careSearchMenuVisible) {
      setShowTopMenus(false);
      const timeout = setTimeout(() => {
        setShowTopMenus(true);
      }, 300); // match the drawer's transition duration
      return () => clearTimeout(timeout);
    }
  }, [careSearchMenuVisible]);

  useEffect(() => {
    if (claimsAndCoverageMenuVisible) {
      setShowTopMenus(false);
      const timeout = setTimeout(() => {
        setShowTopMenus(true);
      }, 300); // match the drawer's transition duration
      return () => clearTimeout(timeout);
    }
  }, [claimsAndCoverageMenuVisible]);

  const offeringViewingAsMenu = availableEligibility.length > 1;

  const onMobileMenuToggle = useCallback(() => {
    setClaimsAndCoverageMenuVisible(false);
    setCareSearchMenuVisible(false);
    setVisible(false);
  }, []);

  const topMenus = isVisible && showTopMenus && (
    <div style={{ top: "0", left: "0", position: "fixed", width: "100%" }}>
      <MobileTopBar />
      <MobileMainBar mobileMenuIcon="x" onMobileMenuToggle={onMobileMenuToggle} />
    </div>
  );

  return (
    <>
      <DrawerOffsetStyle spaceForViewer={offeringViewingAsMenu} />
      <Drawer size="ultra-wide" visible={isVisible}>
        {topMenus}
        <Stack spacing="large">
          {homeItems.map((menuItem, idx) => {
            return (
              <ClientAwareMenuLink key={menuItem.to + idx.toString()} to={menuItem.to} onClick={() => onCommonMenuItemClick(menuItem)}>
                <Heading>{menuItem.displayText}</Heading>
              </ClientAwareMenuLink>
            );
          })}
          <StyledMenuButton
            appearance="text"
            onClick={() => setClaimsAndCoverageMenuVisible(true)}
            icon="chevron-right"
            iconPosition="right"
          >
            <Heading>{translate("MENU_BAR.claimsAndCoverage")}</Heading>
          </StyledMenuButton>
          {careSearchMenuItems.length > 0 && (
            <StyledMenuButton appearance="text" onClick={() => setCareSearchMenuVisible(true)} icon="chevron-right" iconPosition="right">
              <Heading>{translate("MENU_BAR.careSearch")}</Heading>
            </StyledMenuButton>
          )}
          {dynamicLinkItems?.map((menuItem, idx) => {
            if (!menuItem.openBehavior) {
              return (
                <ClientAwareMenuLink
                  key={menuItem.to + idx.toString()}
                  to={menuItem.to}
                  testId={`dynamic-link-menu-item-${idx}`}
                  onClick={() => onCommonMenuItemClick(menuItem)}
                >
                  <Heading>{menuItem.displayText}</Heading>
                </ClientAwareMenuLink>
              );
            }
            if (menuItem.openBehavior === "externalPage") {
              return (
                <MenuLink
                  key={menuItem.to + idx.toString()}
                  onClick={() => onCommonMenuItemClick(menuItem)}
                  testId={`dynamic-link-menu-item-${idx}`}
                  to={menuItem.to}
                >
                  <Heading>
                    <Shelf>
                      {menuItem.displayText}
                      <Icon name="link-out" />
                      <Shelf.Spacer />
                    </Shelf>
                  </Heading>
                </MenuLink>
              );
            }
          })}
          {externalConnectionItems.map((menuItem, idx) => {
            if (!menuItem.openBehavior) {
              return (
                <ClientAwareMenuLink
                  key={menuItem.to + idx.toString()}
                  to={menuItem.to}
                  testId={`external-connection-menu-item-${idx}`}
                  onClick={() => onCommonMenuItemClick(menuItem)}
                >
                  <Heading>{menuItem.displayText}</Heading>
                </ClientAwareMenuLink>
              );
            }
            if (menuItem.openBehavior === "externalPage") {
              return (
                <MenuLink
                  key={menuItem.to + idx.toString()}
                  onClick={() => onCommonMenuItemClick(menuItem)}
                  testId={`external-connection-menu-item-${idx}`}
                  to={menuItem.to}
                >
                  <Heading>
                    <Shelf>
                      {menuItem.displayText}
                      <Icon name="link-out" />
                      <Shelf.Spacer />
                    </Shelf>
                  </Heading>
                </MenuLink>
              );
            }
          })}
        </Stack>
      </Drawer>
      <Drawer size="ultra-wide" visible={claimsAndCoverageMenuVisible}>
        {claimsAndCoverageMenuVisible && topMenus}
        <Stack spacing="large">
          {claimsAndCoverageMenuItems.length > 0 && (
            <NestedMenuHeadingButton icon="chevron-left" appearance="text" onClick={() => setClaimsAndCoverageMenuVisible(false)}>
              <Heading>{translate("MENU_BAR.claimsAndCoverage")}</Heading>
            </NestedMenuHeadingButton>
          )}
          <Stack>
            {claimsAndCoverageMenuItems.map((menuItem, idx) => {
              if (!menuItem.openBehavior) {
                return (
                  <NestedClientAwareMenuLink
                    key={menuItem.to + idx.toString()}
                    to={menuItem.to}
                    testId={`menu-item-${idx}`}
                    onClick={() => onCommonMenuItemClick(menuItem)}
                  >
                    <Regular>{menuItem.displayText}</Regular>
                  </NestedClientAwareMenuLink>
                );
              }
              if (menuItem.openBehavior === "externalPage") {
                return (
                  <NestedMenuLink
                    key={menuItem.to + idx.toString()}
                    onClick={() => onCommonMenuItemClick(menuItem)}
                    testId={`menu-item-${idx}`}
                    to={menuItem.to}
                  >
                    <Shelf>
                      <Regular>{menuItem.displayText}</Regular>
                      <Icon name="link-out" />
                      <Shelf.Spacer />
                    </Shelf>
                  </NestedMenuLink>
                );
              }
            })}
          </Stack>
        </Stack>
      </Drawer>
      <Drawer size="ultra-wide" visible={careSearchMenuVisible}>
        {careSearchMenuVisible && topMenus}
        <Stack spacing="large">
          {careSearchMenuItems.length > 0 && (
            <NestedMenuHeadingButton icon="chevron-left" appearance="text" onClick={() => setCareSearchMenuVisible(false)}>
              <Heading>{translate("MENU_BAR.careSearch")}</Heading>
            </NestedMenuHeadingButton>
          )}
          <Stack>
            {careSearchMenuItems.map((menuItem, idx) => {
              if (!menuItem.openBehavior) {
                return (
                  <NestedClientAwareMenuLink
                    key={menuItem.to + idx.toString()}
                    to={menuItem.to}
                    testId={`menu-item-${idx}`}
                    onClick={() => onCommonMenuItemClick(menuItem)}
                  >
                    <Regular>{menuItem.displayText}</Regular>
                  </NestedClientAwareMenuLink>
                );
              }
              if (menuItem.openBehavior === "externalPage") {
                return (
                  <NestedMenuLink
                    key={menuItem.to + idx.toString()}
                    onClick={() => onCommonMenuItemClick(menuItem)}
                    testId={`menu-item-${idx}`}
                    to={menuItem.to}
                  >
                    <Shelf>
                      <Regular>{menuItem.displayText}</Regular>
                      <Icon name="link-out" />
                      <Shelf.Spacer />
                    </Shelf>
                  </NestedMenuLink>
                );
              }
            })}
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};
